import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { eventClassroomNames, eventScope, getDatePart } from "./utils";

// Gedeelde jaarplanner-Excelexport: gebruikt door de Yearplanner (beheer) en het ouderportaal.
// Wijzig het formaat alleen hier, zodat beide exports gelijk blijven.

const DUTCH_LOCALE = "nl-NL";
const MONTH_TABLE_WIDTH = 3;
const MONTH_GAP_WIDTH = 1;
const MONTHS_PER_BLOCK = 6;
const MAX_DAYS_IN_MONTH = 31;
const BLOCK_HEIGHT = MAX_DAYS_IN_MONTH + 2;
const TOTAL_EXPORT_COLUMNS = MONTHS_PER_BLOCK * MONTH_TABLE_WIDTH + (MONTHS_PER_BLOCK - 1) * MONTH_GAP_WIDTH;

const exportBorder = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
} as any;

const fill = (argb: string) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

const getExportColumn = (monthIndex: number) => monthIndex * (MONTH_TABLE_WIDTH + MONTH_GAP_WIDTH) + 1;

const getDutchMonthName = (month: any) => {
  const firstDayKey = month?.days?.[0]?.key;
  const date = firstDayKey ? new Date(`${firstDayKey}T00:00:00`) : null;

  if (date && !Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat(DUTCH_LOCALE, { month: "long" }).format(date);
  }

  return String(month?.label || "").replace(/\s+\d{4}$/, "");
};

const getDutchWeekday = (day: any) => {
  const date = day?.key ? new Date(`${day.key}T00:00:00`) : null;

  if (date && !Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat(DUTCH_LOCALE, { weekday: "short" }).format(date).replace(".", "");
  }

  return String(day?.weekday || "").replace(".", "");
};

const hasEventType = (day: any, eventTypes: string[]) =>
  day?.events?.some((event: any) => eventTypes.includes(String(event?.eventType || "").toUpperCase()));

const getDayFill = (day: any) => {
  if (hasEventType(day, ["STUDY_DAY"])) return fill("FFFFC7CE");
  if (day?.hasHoliday || hasEventType(day, ["HOLIDAY", "DAY_OFF"])) return fill("FFE2F0D9");
  if (day?.isWeekend) return fill("FFFCE4D6");
  return fill("FFFFFFFF");
};

const formatDutchDayMonth = (dateKey: string) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat(DUTCH_LOCALE, { day: "numeric", month: "long" }).format(date);
};

// Klasactiviteiten krijgen de klas(sen) erachter, bijv. "Kamp (Klas 5)".
// Op de laatste dag van de planner krijgt een item dat daarna doorloopt (zoals de zomervakantie)
// de einddatum erbij, bijv. "Zomervakantie t/m 30 augustus".
export const getExportEventLabel = (
  event: any,
  classroomLookup: Map<string, unknown>,
  dayKey?: string,
  plannerEndKey?: string,
) => {
  let label = event?.title || "-";
  if (eventScope(event) === "CLASSROOM") {
    const classroomNames = eventClassroomNames(event, classroomLookup);
    if (classroomNames.length > 0) label = `${label} (${classroomNames.join(", ")})`;
  }
  const endKey = getDatePart(event?.endDate);
  if (dayKey && plannerEndKey && dayKey === plannerEndKey && endKey && endKey > plannerEndKey) {
    label = `${label} t/m ${formatDutchDayMonth(endKey)}`;
  }
  return label;
};

const sanitizeExcelFilePart = (value: string) =>
  value
    .trim()
    .replace(/[\\/\s]+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const exportYearPlannerToExcel = async ({ yearPlannerMonths, schoolYear, classroomLookup = new Map() }: any) => {
  const lastMonthDays = yearPlannerMonths?.[yearPlannerMonths.length - 1]?.days || [];
  const plannerEndKey: string | undefined = lastMonthDays[lastMonthDays.length - 1]?.key;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Lumen";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(`Jaarplanning ${schoolYear}`.slice(0, 31), {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 2,
      horizontalCentered: true,
      margins: {
        left: 0.25,
        right: 0.25,
        top: 0.35,
        bottom: 0.35,
        header: 0.2,
        footer: 0.2,
      },
    },
  });

  worksheet.mergeCells(1, 1, 1, TOTAL_EXPORT_COLUMNS);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = `${schoolYear} jaarplanner Werfklas`;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 8;

  for (let monthIndex = 0; monthIndex < MONTHS_PER_BLOCK; monthIndex += 1) {
    const startColumn = getExportColumn(monthIndex);
    worksheet.getColumn(startColumn).width = 5;
    worksheet.getColumn(startColumn + 1).width = 5;
    worksheet.getColumn(startColumn + 2).width = 24;

    if (monthIndex < MONTHS_PER_BLOCK - 1) {
      worksheet.getColumn(startColumn + 3).width = 2;
    }
  }

  const writeMonth = (month: any, rowStart: number, monthIndex: number) => {
    const startColumn = getExportColumn(monthIndex);
    const endColumn = startColumn + 2;

    worksheet.mergeCells(rowStart, startColumn, rowStart, endColumn);
    const monthHeader = worksheet.getCell(rowStart, startColumn);
    monthHeader.value = getDutchMonthName(month);
    monthHeader.font = { bold: true, size: 12 };
    monthHeader.alignment = { horizontal: "center", vertical: "middle" };
    monthHeader.fill = fill("FFD9EAF7") as any;

    const headerRow = worksheet.getRow(rowStart + 1);
    headerRow.getCell(startColumn).value = "dag";
    headerRow.getCell(startColumn + 1).value = "#";
    headerRow.getCell(startColumn + 2).value = "planning";

    for (let column = startColumn; column <= endColumn; column += 1) {
      const cell = headerRow.getCell(column);
      cell.font = { bold: true, size: 9 };
      cell.alignment = { horizontal: column === startColumn + 2 ? "left" : "center", vertical: "middle" };
      cell.fill = fill("FFEAF3F8") as any;
    }

    for (let dayOffset = 0; dayOffset < MAX_DAYS_IN_MONTH; dayOffset += 1) {
      const row = worksheet.getRow(rowStart + 2 + dayOffset);
      const day = month?.days?.[dayOffset];
      const planningCell = row.getCell(startColumn + 2);

      row.height = 22;
      row.getCell(startColumn).value = day ? getDutchWeekday(day) : "";
      row.getCell(startColumn + 1).value = day ? Number(day.dayNumber) : "";
      planningCell.value = day?.events?.map((event: any) => getExportEventLabel(event, classroomLookup, day?.key, plannerEndKey)).join("\n") || "";

      for (let column = startColumn; column <= endColumn; column += 1) {
        const cell = row.getCell(column);
        cell.fill = day ? (getDayFill(day) as any) : (fill("FFFFFFFF") as any);
        cell.alignment = {
          horizontal: column === startColumn + 2 ? "left" : "center",
          vertical: "middle",
          wrapText: column === startColumn + 2,
        };
        cell.font = {
          size: 9,
          bold: Boolean(day && hasEventType(day, ["HOLIDAY", "DAY_OFF"])),
          color: day && hasEventType(day, ["STUDY_DAY"]) ? { argb: "FF9C0006" } : { argb: "FF000000" },
        };
      }
    }

    for (let row = rowStart; row < rowStart + BLOCK_HEIGHT; row += 1) {
      for (let column = startColumn; column <= endColumn; column += 1) {
        worksheet.getCell(row, column).border = exportBorder;
      }
    }
  };

  const topMonths = yearPlannerMonths.slice(0, MONTHS_PER_BLOCK);
  const bottomMonths = yearPlannerMonths.slice(MONTHS_PER_BLOCK, MONTHS_PER_BLOCK * 2);
  const topRowStart = 3;
  const bottomRowStart = topRowStart + BLOCK_HEIGHT + 2;

  topMonths.forEach((month: any, index: number) => writeMonth(month, topRowStart, index));
  bottomMonths.forEach((month: any, index: number) => writeMonth(month, bottomRowStart, index));

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `Werfklas_Jaarplanning_${sanitizeExcelFilePart(schoolYear)}.xlsx`;
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
};
